import { SimulationConfig } from '@/types';
import { PerformanceTelemetry } from '@/services/PerformanceTelemetry';

// WGSL Shader definitions for Particle simulation and Instanced presentation
const PARTICLE_COMPUTE_WGSL = `
struct Particle {
    pos_x: f32,
    pos_y: f32,
    vel_x: f32,
    vel_y: f32,
    color_r: f32,
    color_g: f32,
    color_b: f32,
    life: f32,
    maxLife: f32,
    padding1: f32,
    padding2: f32,
    padding3: f32,
};

struct ParticleConfig {
    deltaTime: f32,
    gravity: f32,
    damping: f32,
    maxParticles: f32,
};

@group(0) @binding(0) var<storage, read_write> particles: array<Particle>;
@group(0) @binding(1) var<uniform> config: ParticleConfig;

@compute @workgroup_size(64)
fn cs_main(@builtin(global_invocation_id) gid: vec3<u32>) {
    let index = gid.x;
    if (index >= u32(config.maxParticles)) {
        return;
    }

    var p = particles[index];
    if (p.life <= 0.0) {
        return;
    }

    // Decay lifetime (dt in milliseconds divided by total millisecond duration)
    p.life = p.life - (config.deltaTime / p.maxLife);
    if (p.life < 0.0) {
        p.life = 0.0;
        p.pos_x = -9999.0; // send retired ones far off-screen
        p.pos_y = -9999.0;
    } else {
        // Apply acceleration & damping (drag) forces
        p.vel_y = p.vel_y + config.gravity * config.deltaTime;
        
        let damp = pow(config.damping, config.deltaTime / 16.667);
        p.vel_x = p.vel_x * damp;
        p.vel_y = p.vel_y * damp;

        // Position integrated using simple Euler equations
        p.pos_x = p.pos_x + p.vel_x * config.deltaTime;
        p.pos_y = p.pos_y + p.vel_y * config.deltaTime;
    }

    particles[index] = p;
}
`;

const PARTICLE_RENDER_WGSL = `
struct CameraUniforms {
    resolution: vec2<f32>,
    translate: vec2<f32>,
    scale: f32,
    baseSize: f32,
};

struct Particle {
    pos_x: f32,
    pos_y: f32,
    vel_x: f32,
    vel_y: f32,
    color_r: f32,
    color_g: f32,
    color_b: f32,
    life: f32,
    maxLife: f32,
    padding1: f32,
    padding2: f32,
    padding3: f32,
};

@group(0) @binding(0) var<uniform> global: CameraUniforms;
@group(0) @binding(1) var<storage, read> particles: array<Particle>;

struct VertexOutput {
    @builtin(position) clipPos: vec4<f32>,
    @location(0) color: vec3<f32>,
    @location(1) life: f32,
    @location(2) uv: vec2<f32>,
};

@vertex
fn vs_main(
    @builtin(vertex_index) vertexIndex: u32,
    @builtin(instance_index) instanceIndex: u32
) -> VertexOutput {
    var out: VertexOutput;

    let p = particles[instanceIndex];
    if (p.life <= 0.0) {
        // Discard retired or unactive instances by outputting degenerate off-screen coordinates
        out.clipPos = vec4<f32>(-9999.0, -9999.0, 0.0, 1.0);
        out.color = vec3<f32>(0.0);
        out.life = 0.0;
        out.uv = vec2<f32>(0.0);
        return out;
    }

    // Procedural 2D Quads coordinates (eliminates the need for GPU input buffer transfers)
    var positions = array<vec2<f32>, 6>(
        vec2<f32>(-0.5, -0.5),
        vec2<f32>( 0.5, -0.5),
        vec2<f32>(-0.5,  0.5),
        vec2<f32>(-0.5,  0.5),
        vec2<f32>( 0.5, -0.5),
        vec2<f32>( 0.5,  0.5)
    );

    let quad_pos = positions[vertexIndex];
    
    // Scale size by normalized lifetime (decaying from 1.0 to 0.0)
    let size = global.baseSize * max(0.0, p.life);

    let world_pos = vec2<f32>(p.pos_x, p.pos_y);
    let camPos = (world_pos * global.scale) + global.translate;
    let vertex_pos_pixels = camPos + (quad_pos * size * global.scale);

    let normalized = (vertex_pos_pixels / global.resolution) * 2.0 - vec2<f32>(1.0, 1.0);
    out.clipPos = vec4<f32>(normalized.x, -normalized.y, 0.0, 1.0);
    out.color = vec3<f32>(p.color_r, p.color_g, p.color_b);
    out.life = p.life;
    out.uv = quad_pos; // Centered local uv coordinates ranging from -0.5 to 0.5
    return out;
}

@fragment
fn fs_main(in: VertexOutput) -> @location(0) vec4<f32> {
    let dist = length(in.uv);
    if (dist > 0.5) {
        discard;
    }

    // High fidelity aesthetic glowing neon circle decay matching WebGL shaders
    let glow = exp(-3.8 * dist);
    let alpha = smoothstep(0.5, 0.1, dist);
    return vec4<f32>(in.color, glow * in.life * alpha);
}
`;

export class WebGPUParticleSystem {
  private device: any;
  private format: string;

  private computePipeline: any = null;
  private renderPipeline: any = null;

  // GPU Buffers
  private particlesBuffer: any = null;
  private computeConfigBuffer: any = null;
  private renderConfigBuffer: any = null;

  // Bind groups
  private computeBindGroup: any = null;
  private renderBindGroup: any = null;

  // System parameters
  private readonly maxParticles = 50000; // Static capacity to eliminate spikes
  private activeLimit = 25000;           // Dynamically configured to adapt based on profiling
  private writeHead = 0;                  // Rolling circular queue writing index

  // Pre-allocated emission pool to prevent Garbage Collection (GC) pauses
  private emitBuffer: Float32Array;

  constructor(device: any, format: string) {
    this.device = device;
    this.format = format;

    // 12 floats per Particle: [pos.x, pos.y, vel.x, vel.y, r, g, b, life, maxLife, pad, pad, pad]
    this.emitBuffer = new Float32Array(5000 * 12);

    this.createGPUResources();
    this.compilePipelines();
    this.createBindGroups();
  }

  private createGPUResources() {
    const bytesPerParticle = 12 * 4; // 48 bytes
    const totalBytes = this.maxParticles * bytesPerParticle;

    // Pre-allocate completely single unified storage memory pool structure for all particles
    this.particlesBuffer = this.device.createBuffer({
      label: 'GPU Particle Pool Storage Buffer',
      size: totalBytes,
      usage: 0x0080 | 0x0008 // STORAGE | COPY_DST
    });

    // Zero-out the storage buffer
    const zeroData = new Float32Array(this.maxParticles * 12);
    this.device.queue.writeBuffer(this.particlesBuffer, 0, zeroData);

    // Dynamic compute config (Size 16 bytes = 4 floats)
    this.computeConfigBuffer = this.device.createBuffer({
      label: 'GPU Particle Compute Uniform Buffer',
      size: 16,
      usage: 0x0040 | 0x0008 // UNIFORM | COPY_DST
    });

    // Renderer uniforms (Size 32 bytes aligned = 8 floats: res.x, res.y, trans.x, trans.y, scale, baseSize, pad, pad)
    this.renderConfigBuffer = this.device.createBuffer({
      label: 'GPU Particle Render Uniform Buffer',
      size: 32,
      usage: 0x0040 | 0x0008 // UNIFORM | COPY_DST
    });
  }

  private compilePipelines() {
    const compModule = this.device.createShaderModule({
      label: 'Particle Compute Shader',
      code: PARTICLE_COMPUTE_WGSL
    });

    const rendModule = this.device.createShaderModule({
      label: 'Particle Render Shader',
      code: PARTICLE_RENDER_WGSL
    });

    // Compute layout & pipeline compilation
    const computeLayout = this.device.createBindGroupLayout({
      label: 'Particle Compute Bind Group Layout',
      entries: [
        { binding: 0, visibility: 4, buffer: { type: 'storage' } }, // COMPUTE
        { binding: 1, visibility: 4, buffer: { type: 'uniform' } }
      ]
    });

    this.computePipeline = this.device.createComputePipeline({
      label: 'Particle Update Compute Pipeline',
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [computeLayout] }),
      compute: { module: compModule, entryPoint: 'cs_main' }
    });

    // Render layout & pipeline compilation
    const renderLayout = this.device.createBindGroupLayout({
      label: 'Particle Render Bind Group Layout',
      entries: [
        { binding: 0, visibility: 1, buffer: { type: 'uniform' } }, // VERTEX
        { binding: 1, visibility: 1, buffer: { type: 'read-only-storage' } }
      ]
    });

    this.renderPipeline = this.device.createRenderPipeline({
      label: 'Particle Draw Render Pipeline',
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [renderLayout] }),
      vertex: {
        module: rendModule,
        entryPoint: 'vs_main'
      },
      fragment: {
        module: rendModule,
        entryPoint: 'fs_main',
        targets: [{
          format: this.format as any,
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one', operation: 'add' }, // Additive neon blend
            alpha: { srcFactor: 'zero', dstFactor: 'one', operation: 'add' }
          }
        }]
      },
      primitive: {
        topology: 'triangle-list'
      }
    });
  }

  private createBindGroups() {
    this.computeBindGroup = this.device.createBindGroup({
      label: 'Particle Compute Bind Group',
      layout: this.computePipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.particlesBuffer } },
        { binding: 1, resource: { buffer: this.computeConfigBuffer } }
      ]
    });

    this.renderBindGroup = this.device.createBindGroup({
      label: 'Particle Render Bind Group',
      layout: this.renderPipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.renderConfigBuffer } },
        { binding: 1, resource: { buffer: this.particlesBuffer } }
      ]
    });
  }

  public emit(posX: number, posY: number, colorHex: string, count = 20, speedMult = 1.0, duration = 800) {
    if (!this.device) return;

    // Convert hex code directly into normalized RGB channels
    const r = parseInt(colorHex.slice(1, 3), 16) / 255 || 1.0;
    const g = parseInt(colorHex.slice(3, 5), 16) / 255 || 1.0;
    const b = parseInt(colorHex.slice(5, 7), 16) / 255 || 1.0;

    // Bound emit block to available capacity space in one frame
    const actualCount = Math.min(count, this.activeLimit, 5000);
    if (actualCount <= 0) return;

    const stride = 12;
    for (let i = 0; i < actualCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (2.0 + Math.random() * 8.0) * speedMult * 0.12;

      const offset = i * stride;
      this.emitBuffer[offset + 0] = posX;
      this.emitBuffer[offset + 1] = posY;
      this.emitBuffer[offset + 2] = Math.cos(angle) * speed;
      this.emitBuffer[offset + 3] = Math.sin(angle) * speed;
      this.emitBuffer[offset + 4] = r;
      this.emitBuffer[offset + 5] = g;
      this.emitBuffer[offset + 6] = b;
      this.emitBuffer[offset + 7] = 1.0;      // life initially 1.0
      this.emitBuffer[offset + 8] = duration; // duration in milliseconds
      this.emitBuffer[offset + 9] = 0;        // padding
      this.emitBuffer[offset + 10] = 0;       // padding
      this.emitBuffer[offset + 11] = 0;       // padding
    }

    // Direct memory pointer injection onto GPU device queue
    const bytesPerParticle = stride * 4;
    const emitBytesSize = actualCount * bytesPerParticle;
    const writeByteOffset = this.writeHead * bytesPerParticle;

    if (this.writeHead + actualCount <= this.maxParticles) {
      // Simple contiguous write
      this.device.queue.writeBuffer(
        this.particlesBuffer,
        writeByteOffset,
        this.emitBuffer,
        0,
        actualCount * stride
      );
    } else {
      // Wrap-around write split into 2 block operations due to circular queue limits
      const firstPartCount = this.maxParticles - this.writeHead;
      const secondPartCount = actualCount - firstPartCount;

      this.device.queue.writeBuffer(
        this.particlesBuffer,
        writeByteOffset,
        this.emitBuffer,
        0,
        firstPartCount * stride
      );

      this.device.queue.writeBuffer(
        this.particlesBuffer,
        0,
        this.emitBuffer,
        firstPartCount * stride,
        secondPartCount * stride
      );
    }

    // Update circular queue head position pointer
    this.writeHead = (this.writeHead + actualCount) % this.activeLimit;

    // Collect metrics
    PerformanceTelemetry.recordVertexUpload(actualCount, emitBytesSize);
    PerformanceTelemetry.bytesUploaded += emitBytesSize;
  }

  public updateAndRender(config: SimulationConfig, camera: any, dtMs: number, renderTargetView: any) {
    if (!this.device || !renderTargetView) return;

    // --- Task 5.3: Adaptively scale the active limits depending on frame execution scores ---
    const scoreFps = PerformanceTelemetry.fps;
    let targetLimit = 25000;
    if (scoreFps > 54) {
      targetLimit = 50000;
    } else if (scoreFps < 45) {
      targetLimit = 15000;
    }
    // Smoothly interpolate active limit transitions to eliminate sudden jumps
    this.activeLimit = Math.round(this.activeLimit * 0.95 + targetLimit * 0.05);

    // --- EXECUTE TASK 5.1: COMPUTE PHYSICS LAWS UPDATE ---
    const gravity = config.collisionEffectGravity !== undefined ? config.collisionEffectGravity * 0.001 : 0.015;
    const damping = 0.985;

    const computeConfig = new Float32Array(4);
    computeConfig[0] = dtMs;
    computeConfig[1] = gravity;
    computeConfig[2] = damping;
    computeConfig[3] = this.activeLimit;

    this.device.queue.writeBuffer(this.computeConfigBuffer, 0, computeConfig);

    const commandEncoder = this.device.createCommandEncoder({ label: 'Particle Simulation encoder' });
    const computePass = commandEncoder.beginComputePass({ label: 'Particle Physics Pass' });
    computePass.setPipeline(this.computePipeline);
    computePass.setBindGroup(0, this.computeBindGroup);
    computePass.dispatchWorkgroups(Math.ceil(this.activeLimit / 64));
    computePass.end();

    // --- EXECUTE TASK 5.2: HIGH-THROUGHPUT GRAPHICS DRAW ---
    const renderConfig = new Float32Array(8);
    renderConfig[0] = this.device.canvasWidth || 1920; // fallback standard widths
    renderConfig[1] = this.device.canvasHeight || 1080;
    if (camera) {
      renderConfig[0] = camera.resolutionWidth || renderConfig[0];
      renderConfig[1] = camera.resolutionHeight || renderConfig[1];
      // Adapt translates & scale bounds
      renderConfig[2] = camera.x !== undefined ? camera.x : 0;
      renderConfig[3] = camera.y !== undefined ? camera.y : 0;
      renderConfig[4] = camera.scale !== undefined ? camera.scale : 1.0;
    }
    renderConfig[5] = config.collisionEffectScale !== undefined ? config.collisionEffectScale * 4.0 : 4.0;

    this.device.queue.writeBuffer(this.renderConfigBuffer, 0, renderConfig);

    const renderPassDescriptor: any = {
      label: 'Additively Glowing Particles Rendering',
      colorAttachments: [{
        view: renderTargetView,
        loadOp: 'load',
        storeOp: 'store'
      }]
    };

    const renderPass = commandEncoder.beginRenderPass(renderPassDescriptor);
    renderPass.setPipeline(this.renderPipeline);
    renderPass.setBindGroup(0, this.renderBindGroup);
    
    // Draw 6 quad vertices per particle, instanced across the currently active limits
    renderPass.draw(6, this.activeLimit);
    renderPass.end();

    this.device.queue.submit([commandEncoder.finish()]);

    // Telemetry updates
    PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
    PerformanceTelemetry.recordIndexCount(6 * this.activeLimit);
    PerformanceTelemetry.vramDetails.storageBuffers += this.maxParticles * 12 * 4;
    PerformanceTelemetry.vramDetails.uniformBuffers += 16 + 32;
    PerformanceTelemetry.bytesUploaded += computeConfig.byteLength + renderConfig.byteLength;
  }

  public updateAndRenderWithEncoder(config: SimulationConfig, camera: any, dtMs: number, renderTargetView: any, commandEncoder: any) {
    if (!this.device || !renderTargetView || !commandEncoder) return;

    // --- Task 5.3: Adaptively scale the active limits depending on frame execution scores ---
    const scoreFps = PerformanceTelemetry.fps;
    let targetLimit = 25000;
    if (scoreFps > 54) {
      targetLimit = 50000;
    } else if (scoreFps < 45) {
      targetLimit = 15000;
    }
    this.activeLimit = Math.round(this.activeLimit * 0.95 + targetLimit * 0.05);

    // --- EXECUTE TASK 5.1: COMPUTE PHYSICS LAWS UPDATE ---
    const gravity = config.collisionEffectGravity !== undefined ? config.collisionEffectGravity * 0.001 : 0.015;
    const damping = 0.985;

    const computeConfig = new Float32Array(4);
    computeConfig[0] = dtMs;
    computeConfig[1] = gravity;
    computeConfig[2] = damping;
    computeConfig[3] = this.activeLimit;

    this.device.queue.writeBuffer(this.computeConfigBuffer, 0, computeConfig);

    const computePass = commandEncoder.beginComputePass({ label: 'Particle Physics Pass (RenderGraph-orchestrated)' });
    computePass.setPipeline(this.computePipeline);
    computePass.setBindGroup(0, this.computeBindGroup);
    computePass.dispatchWorkgroups(Math.ceil(this.activeLimit / 64));
    computePass.end();

    // --- EXECUTE TASK 5.2: HIGH-THROUGHPUT GRAPHICS DRAW ---
    const renderConfig = new Float32Array(8);
    renderConfig[0] = this.device.canvasWidth || 1920;
    renderConfig[1] = this.device.canvasHeight || 1080;
    if (camera) {
      renderConfig[0] = camera.resolutionWidth || renderConfig[0];
      renderConfig[1] = camera.resolutionHeight || renderConfig[1];
      renderConfig[2] = camera.x !== undefined ? camera.x : 0;
      renderConfig[3] = camera.y !== undefined ? camera.y : 0;
      renderConfig[4] = camera.scale !== undefined ? camera.scale : 1.0;
    }
    renderConfig[5] = config.collisionEffectScale !== undefined ? config.collisionEffectScale * 4.0 : 4.0;

    this.device.queue.writeBuffer(this.renderConfigBuffer, 0, renderConfig);

    const renderPassDescriptor: any = {
      label: 'Additively Glowing Particles Rendering (RenderGraph-orchestrated)',
      colorAttachments: [{
        view: renderTargetView,
        loadOp: 'load',
        storeOp: 'store'
      }]
    };

    const renderPass = commandEncoder.beginRenderPass(renderPassDescriptor);
    renderPass.setPipeline(this.renderPipeline);
    renderPass.setBindGroup(0, this.renderBindGroup);
    renderPass.draw(6, this.activeLimit);
    renderPass.end();

    // Telemetry updates
    PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
    PerformanceTelemetry.recordIndexCount(6 * this.activeLimit);
    PerformanceTelemetry.vramDetails.storageBuffers += this.maxParticles * 12 * 4;
    PerformanceTelemetry.vramDetails.uniformBuffers += 16 + 32;
    PerformanceTelemetry.bytesUploaded += computeConfig.byteLength + renderConfig.byteLength;
  }

  public reset() {
    if (!this.device) return;
    this.writeHead = 0;
    const zeroData = new Float32Array(this.maxParticles * 12);
    this.device.queue.writeBuffer(this.particlesBuffer, 0, zeroData);
  }

  public dispose() {
    if (this.particlesBuffer) {
      this.particlesBuffer.destroy();
      this.particlesBuffer = null;
    }
    if (this.computeConfigBuffer) {
      this.computeConfigBuffer.destroy();
      this.computeConfigBuffer = null;
    }
    if (this.renderConfigBuffer) {
      this.renderConfigBuffer.destroy();
      this.renderConfigBuffer = null;
    }
    this.computeBindGroup = null;
    this.renderBindGroup = null;
    this.computePipeline = null;
    this.renderPipeline = null;
  }
}
